import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Anchor, BadgeCheck } from "lucide-react";
import { API } from "@/lib/config";
import { useMember, errorMessage } from "@/components/community/useMember";
import { ApplicantCard } from "@/components/community/ApplicantCard";
import { AuditionModal } from "@/components/community/AuditionModal";
import { ApplyModal } from "@/components/community/ApplyModal";
import { AccessModal } from "@/components/community/AccessModal";
import { MemberVideos } from "@/components/community/MemberVideos";
import { Leaderboard } from "@/components/community/Leaderboard";
import { InquiryModal } from "@/components/community/InquiryModal";

const FILTERS = [
  { label: "All", role: "All" },
  { label: "Captains", role: "Captain" },
  { label: "First Mates", role: "First Mate" },
  { label: "Yachts", role: "Yacht" },
];

export const CommunityHub = ({ onWatch }) => {
  const { member, headers, signIn, signOut, setMember } = useMember();
  const [applicants, setApplicants] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [inquiry, setInquiry] = useState(null);

  useEffect(() => {
    axios.get(`${API}/applicants`)
      .then((r) => setApplicants(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Couldn't reach the crew list — please refresh."));
  }, []);

  const visible = useMemo(
    () => (filter === "All" ? applicants : applicants.filter((a) => a.role === filter)),
    [applicants, filter]
  );
  const voted = new Set(member?.voted_ids || []);
  const followed = new Set(member?.followed_ids || []);

  const act = async (a, kind) => {
    if (!member) { setAccessOpen(true); return; }
    const listKey = kind === "vote" ? "voted_ids" : "followed_ids";
    if (member[listKey].includes(a.id)) return;
    try {
      const { data } = await axios.post(`${API}/applicants/${a.id}/${kind}`, null, { headers });
      setApplicants((prev) => prev.map((p) => (p.id === a.id ? data : p)));
      setSelected((s) => (s && s.id === a.id ? data : s));
      setMember((m) => ({ ...m, [listKey]: [...m[listKey], a.id] }));
      toast.success(kind === "vote" ? `Vote logged for ${a.name}` : `Following ${a.name}`);
    } catch (err) {
      if (err?.response?.status === 401) { signOut(); setAccessOpen(true); return; }
      toast.error(errorMessage(err, "The Channel is rough — try again."));
    }
  };

  const inquire = (a) => { if (!member) { setAccessOpen(true); return; } setSelected(null); setInquiry(a); };

  return (
    <section id="community" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36" data-testid="community-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div className="max-w-xl">
          <p className="text-[11px] tracking-[0.5em] uppercase text-[#D4AF37]">The Crew Wants You</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-[0.08em] text-[#F5F3EF] mt-3" data-testid="community-heading">
            FOLLOW. VOTE. <span className="gold-text">APPLY.</span>
          </h2>
          <p className="text-sm md:text-base text-[#94A3B8] mt-4 leading-relaxed" data-testid="community-cta-text">
            Hopeful captains and crew post auditions here. Your votes decide who earns a berth on Captain's Cut,
            the show, and who gets cut.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          {!member && (
            <button onClick={() => setAccessOpen(true)}
              className="inline-flex items-center justify-center gap-3 border border-[#D4AF37]/50 text-[#E8C15A] font-display text-sm tracking-[0.2em] px-8 py-4 hover:bg-[#D4AF37]/10 transition-colors duration-300"
              data-testid="access-open-btn">
              <BadgeCheck size={16} /> FOLLOW &amp; VOTE
            </button>
          )}
          <button id="apply" onClick={() => setApplyOpen(true)}
            className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#070A0F] font-display text-sm tracking-[0.2em] px-8 py-4 hover:bg-[#F3E08A] transition-colors duration-300"
            data-testid="apply-open-btn">
            <Anchor size={16} /> APPLY FOR THE SHOW
          </button>
        </div>
      </div>

      <Leaderboard applicants={applicants} onOpen={setSelected} />

      <div className="flex flex-wrap gap-2 mt-12" data-testid="role-filters">
        {FILTERS.map((f) => (
          <button key={f.role} onClick={() => setFilter(f.role)}
            className={`text-[11px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${
              filter === f.role ? "border-[#D4AF37] text-[#070A0F] bg-[#D4AF37]" : "border-[#D4AF37]/25 text-[#94A3B8] hover:text-[#E8C15A] hover:border-[#D4AF37]/60"
            }`}
            data-testid={`filter-${f.role.toLowerCase().replace(/\s+/g, "-")}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6 mt-10" data-testid="applicant-grid">
        {visible.map((a, i) => (
          <ApplicantCard key={a.id} a={a} i={i} voted={voted.has(a.id)} followed={followed.has(a.id)}
            onVote={(x) => act(x, "vote")} onFollow={(x) => act(x, "follow")} onOpen={setSelected} onInquire={inquire} />
        ))}
        {visible.length === 0 && (
          <p className="col-span-full text-sm text-[#94A3B8] font-editorial italic" data-testid="applicants-empty">
            No {filter.toLowerCase()}s have posted yet — be the first.
          </p>
        )}
      </div>

      <MemberVideos member={member} headers={headers} onUnlock={() => setAccessOpen(true)} onWatch={onWatch} signOut={signOut} />

      <AuditionModal applicant={selected} onClose={() => setSelected(null)} onVote={(a) => act(a, "vote")} voted={!!selected && voted.has(selected.id)} onInquire={inquire} />
      <InquiryModal yacht={inquiry} onClose={() => setInquiry(null)} headers={headers} />
      <AccessModal open={accessOpen} onClose={() => setAccessOpen(false)} signIn={signIn} />
      <ApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} member={member} headers={headers} signIn={signIn} setMember={setMember}
        onCreated={(a) => setApplicants((prev) => [a, ...prev])} />
    </section>
  );
};
